<script>
  import { postJson } from '../../lib/api.js'

  let {
    postUrl = '/users',
    resendConfirmationPath = '/users/confirmation/new',
    translations = {}
  } = $props()

  // Fallback to German defaults if no translations provided
  const t = (key, fallback) => translations[key] || fallback

  let email = $state('')
  let password = $state('')
  let passwordConfirmation = $state('')
  let acceptsTerms = $state(false)
  let submitting = $state(false)
  let succeeded = $state(false)
  let confirmedEmail = $state('')
  let baseError = $state(null)
  let fieldErrors = $state({})

  let canSubmit = $derived(
    email.length > 0 &&
    password.length > 0 &&
    passwordConfirmation.length > 0 &&
    password === passwordConfirmation &&
    acceptsTerms &&
    !submitting
  )

  async function handleSubmit(event) {
    event.preventDefault()
    baseError = null
    fieldErrors = {}

    if (password !== passwordConfirmation) {
      fieldErrors = { password_confirmation: [t('error_password_mismatch', 'Die Passwörter stimmen nicht überein.')] }
      return
    }

    submitting = true

    try {
      const response = await postJson(postUrl, {
        user: {
          email,
          password,
          password_confirmation: passwordConfirmation,
          accepts_terms: acceptsTerms,
        },
      })

      const data = await response.json()

      if (response.ok && data.success) {
        succeeded = true
        confirmedEmail = data.email ?? email
      } else {
        fieldErrors = data.errors ?? {}
        if (!data.errors || Object.keys(data.errors).length === 0) {
          // data.error traegt Fehler ohne Feldbezug -- z. B. die 429-Antwort des
          // Rate-Limiters (RATELIMIT-001, Falle 4). Ohne diesen Zweig kaeme sie
          // als "Verbindungsfehler" an, also als vermeintliches Netzproblem.
          baseError = data.error ?? t('error_connection', 'Verbindungsfehler. Bitte erneut versuchen.')
        }
      }
    } catch {
      baseError = t('error_connection', 'Verbindungsfehler. Bitte erneut versuchen.')
    } finally {
      submitting = false
    }
  }
</script>

<div class="registration-card">
  {#if succeeded}
    <h2 class="registration-title">{t('title', 'Registrieren')}</h2>
    <div class="success-message" role="status">
      {t('success_check_email', 'Fast geschafft! Wir haben Ihnen eine E-Mail geschickt. Bitte bestätigen Sie Ihre Registrierung über den Link darin.')}
    </div>
    <div class="resend-hint">
      <a href={resendConfirmationPath} class="resend-link">
        {t('resend_confirmation', 'Keine E-Mail erhalten? Bestätigung erneut anfordern')}
      </a>
    </div>
  {:else}
    <h2 class="registration-title">{t('title', 'Registrieren')}</h2>

    <form onsubmit={handleSubmit} novalidate>
      {#if baseError}
        <div class="error-message" role="alert">
          {baseError}
        </div>
      {/if}

      <div class="form-group">
        <label for="email" class="form-label">{t('email_label', 'E-Mail')}</label>
        <input
          id="email"
          type="email"
          class="form-input"
          bind:value={email}
          autocomplete="email"
          placeholder={t('email_placeholder', 'ihre@email.de')}
          disabled={submitting}
        />
        {#if fieldErrors.email}
          {#each fieldErrors.email as message}
            <div class="field-error" role="alert">{message}</div>
          {/each}
        {/if}
      </div>

      <div class="form-group">
        <label for="password" class="form-label">{t('password_label', 'Passwort')}</label>
        <input
          id="password"
          type="password"
          class="form-input"
          bind:value={password}
          autocomplete="new-password"
          placeholder={t('password_placeholder', '••••••••')}
          disabled={submitting}
        />
        {#if fieldErrors.password}
          {#each fieldErrors.password as message}
            <div class="field-error" role="alert">{message}</div>
          {/each}
        {/if}
      </div>

      <div class="form-group">
        <label for="password_confirmation" class="form-label">{t('password_confirmation_label', 'Passwort bestätigen')}</label>
        <input
          id="password_confirmation"
          type="password"
          class="form-input"
          bind:value={passwordConfirmation}
          autocomplete="new-password"
          placeholder={t('password_placeholder', '••••••••')}
          disabled={submitting}
        />
        {#if fieldErrors.password_confirmation}
          {#each fieldErrors.password_confirmation as message}
            <div class="field-error" role="alert">{message}</div>
          {/each}
        {/if}
      </div>

      <div class="form-group">
        <label class="terms-label">
          <input type="checkbox" bind:checked={acceptsTerms} disabled={submitting} />
          <span>{t('terms_label', 'Mit der Registrierung nehme ich die Nutzungsbedingungen an')}</span>
        </label>
        {#if fieldErrors.accepts_terms}
          {#each fieldErrors.accepts_terms as message}
            <div class="field-error" role="alert">{message}</div>
          {/each}
        {/if}
      </div>

      <button type="submit" class="submit-button" disabled={!canSubmit}>
        {submitting ? t('submitting', 'Registrieren…') : t('submit', 'Registrieren')}
      </button>
    </form>
  {/if}
</div>

<style>
  .registration-card {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 12px;
    padding: 2rem;
    width: 100%;
    max-width: 400px;
    color: #111827;
  }

  .registration-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    color: #111827;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .terms-label {
    display:     flex;
    align-items: flex-start;
    gap:         0.5rem;
    font-size:   0.875rem;
    line-height: 1.4;
    color:       #374151;
    cursor:      pointer;
  }

  .form-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 0.375rem;
    color: #374151;
  }

  .form-input {
    width: 100%;
    padding: 0.625rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 1rem;
    color: #111827;
    background: #fff;
    box-sizing: border-box;
    transition: border-color 0.15s;
  }

  .form-input:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  .form-input:disabled {
    background: #f9fafb;
    color: #9ca3af;
  }

  .field-error {
    margin-top: 0.375rem;
    font-size: 0.8125rem;
    color: #dc2626;
  }

  .error-message {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 6px;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
    font-size: 0.875rem;
    color: #dc2626;
  }

  .success-message {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 6px;
    padding: 0.75rem 1rem;
    font-size: 0.9375rem;
    color: #166534;
  }

  .resend-hint {
    margin-top: 1rem;
    text-align: center;
  }

  .resend-link {
    font-size: 0.875rem;
    color: #2563eb;
    text-decoration: underline;
  }

  .submit-button {
    width: 100%;
    padding: 0.75rem;
    background: #2563eb;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
  }

  .submit-button:hover:not(:disabled) {
    background: #1d4ed8;
  }

  .submit-button:disabled {
    background: #93c5fd;
    cursor: not-allowed;
  }
</style>
